import { Injectable } from '@angular/core';
import { ToastMessage, ToastMessageData, ToastMessageOptions, ToastMessageOverrides } from '@common/models';
import { v4 as uuid } from 'uuid';

/** Tiempo en pantalla por defecto de los avisos que se cierran solos. */
const DEFAULT_TOAST_DELAY_MS = 4000;

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    constructor() {}

    toasts: ToastMessage[] = [];

    show(header: ToastMessageData, body: ToastMessageData, options: ToastMessageOptions) {
        this.toasts.push({ header, body, uuid: uuid(), options });
    }

    remove(toastID: string) {
        this.toasts = this.toasts.filter(toast => toast.uuid !== toastID);
    }

    success(header: ToastMessageData, body: ToastMessageData) {
        this.replaceToasts(header, body, {
            autohide: true,
            delay: DEFAULT_TOAST_DELAY_MS,
            color: '#1aa179',
            type: 'success',
            bg: '#d2f4ea',
            icon: 'circle-check',
        });
    }

    info(header: ToastMessageData, body: ToastMessageData) {
        this.replaceToasts(header, body, {
            autohide: true,
            delay: DEFAULT_TOAST_DELAY_MS,
            color: '#0d6efd',
            type: 'info',
            bg: '#cfe2ff',
            icon: 'circle-info',
        });
    }

    // Sirve para mantener un mensaje estatico. Con `delay` (ms) el toast se auto-cierra a
    // ese tiempo (autohide); sin `delay` queda estatico (comportamiento original).
    alert(header: ToastMessageData, body: ToastMessageData, delay?: number) {
        this.replaceToasts(header, body, {
            autohide: typeof delay === 'number',
            delay,
            color: '#FFD95A',
            type: 'info',
            bg: '#FFF7D4',
            icon: 'circle-info',
        });
    }

    /**
     * `overrides` permite alargar la lectura o proteger el aviso sin cambiar la duración del resto
     * de la aplicación: quien conoce el mensaje sabe cuánto tarda en leerse.
     */
    error(header: ToastMessageData, body: ToastMessageData, overrides?: ToastMessageOverrides) {
        this.replaceToasts(header, body, {
            autohide: true,
            delay: overrides?.delay ?? DEFAULT_TOAST_DELAY_MS,
            keepOnNewToast: overrides?.keepOnNewToast,
            color: '#dc3545',
            type: 'danger',
            bg: '#f8d7da',
            icon: 'circle-xmark',
        });
    }

    /**
     * Deja en pantalla un único aviso: el nuevo reemplaza a los anteriores. Se exceptúan los
     * marcados con `keepOnNewToast`, que sobreviven hasta agotar su propio `delay` para que un
     * aviso posterior no los borre a media lectura. Se preserva la REFERENCIA del objeto porque
     * el `*ngFor` de la vista rastrea por identidad: crear uno nuevo reiniciaría su temporizador.
     * Un aviso protegido repetido (p. ej. reintentar la impresión bloqueada) no se apila: se
     * descarta el anterior y el nuevo reinicia la ventana de lectura.
     */
    private replaceToasts(header: ToastMessageData, body: ToastMessageData, options: ToastMessageOptions) {
        const preserved = this.toasts.filter(toast => toast.options?.keepOnNewToast && !(toast.header === header && toast.body === body));
        this.toasts = [...preserved, { header, body, uuid: uuid(), options }];
    }
}
